import { joinReferenceId } from '@/lib/utils/id';

// Edit declaration form values → the body POST/PUT /:module/transactions/declaration/:id takes.

// The form's own blanks must not overwrite a stored value with an empty string.
const text = (value) => (value === '' || value === undefined ? undefined : value);
const number = (value) =>
  value === '' || value === null || value === undefined ? undefined : Number(value);

/**
 * `siteId` resolves the site type server-side, so the strategy knows which fields to read.
 * `declarationFileId` links the PDF uploaded just before this call; omitted when that failed.
 */
export const buildDeclarationDto = (values = {}, { declarationFileId } = {}) => ({
  sustainabilityDeclarationNumber: text(joinReferenceId(values)),
  issuanceDate: text(values.dateOfIssuance),
  dispatchDate: text(values.dateOfDispatch),
  siteId: number(values.siteId),

  supplierName: text(values.supplierName),
  supplierAddress: text(values.supplierAddress),
  supplierAddressOfDispatch: text(values.supplierDispatchAddress),
  supplierCertificateNumber: text(values.supplierCertificateNumber),
  recipient: text(values.recipientName),
  recipientAddress: text(values.recipientAddress),
  recipientContractNumber: text(values.contractNumber),
  recipientAddressOfDispatch: text(values.recipientReceiptAddress),

  typeOfProduct: text(values.typeOfProduct),
  typeOfRawMaterial: text(values.rawMaterialType),
  additionalInformation: text(values.additionalInformation),
  countryOfOrigin: text(values.countryOfOrigin),
  totalCertificatedQuantity: number(values.totalCertifiedQuantity),
  unit: text(values.unit),
  energyContent: number(values.energyContent),

  isccCompliantMaterial: text(values.isccCompliantMaterial),
  euRedCompliantMaterial: text(values.euRedCompliantMaterial),
  chainOfCustodyOption: text(values.chainOfCustodyOption),
  countryOfBiofuelProduction: text(values.countryOfBiofuelProduction),
  startDateOfBiofuelProduction: text(values.startDateOfBiofuelProduction),
  // The strategy reads the bioliquid date under this name, not `startDateOfBioliquid`.
  applicationStartOfBiofuelLiquid: text(values.startDateOfBioliquidUse),
  rawMaterialCompliesWithEURed2: text(values.sustainabilityCriteriaCompliance),
  agriculturalAsIntermediateMaterial: text(values.intermediateCrop),
  agricultureBioMassAdditionallyFulfillsILUC: text(values.lowIlucRisk),
  rawMaterialMeetsDefinitionOfWasteOrResidueAccordingToTheRED3: text(values.wasteOrResidue),
  wasteOrAnimalByProductPermitNumber: text(values.wastePermitNumber),
  wasSupportForTheProductionOfTheFuelOrFuelPrecursorReceived: text(values.supportReceived),
  supportNatureAndScheme: text(values.supportNatureAndScheme),

  defaultValueAccordingToRed2Applied: text(values.defaultValueApplied),
  totalGhgEmissions: number(values.totalGhgEmissions),
  eecEmissions: number(values.eec),
  elEmissions: number(values.el),
  epEmissions: number(values.ep),
  etdEmissions: number(values.etd),
  euEmissions: number(values.eu),
  escaEmissions: number(values.esca),
  eccsEmissions: number(values.eccs),
  eccrEmissions: number(values.eccr),
  shippingDistanceKm: number(values.shippingDistanceKm),
  trainDistanceKm: number(values.trainDistanceKm),
  roadDistanceKm: number(values.roadDistanceKm),
  disaggregatedDefaultValueForOil: text(values.disaggregatedDefaultValueForOil),
  disaggregatedDefaultValueForSoil: text(values.disaggregatedDefaultValueForSoil),
  allocatedHeat: number(values.allocatedHeat),
  allocatedElectricity: number(values.allocatedElectricity),
  escaBonusAndCap: text(values.escaBonusCap),
  elBonusObtained: text(values.elBonusObtained),
  nuts2Area: text(values.nuts2Area),

  biofuelsForTransportPercentage: number(values.savingTransport),
  bioliquidsForElectricityPercentage: number(values.savingElectricity),
  biomassForElectricityOutermostRegionsPercentage: number(values.savingElectricityOutermost),
  bioliquidsForHeatPercentage: number(values.savingUsefulHeat),
  biomassForHeatCoalSubstitutionPercentage: number(values.savingHeatSubstitution),

  addOnFoodSecurityStandard: text(values.foodSecurityStandard),
  // The strategy writes both columns; only `eudrRegulations` is read by every site type.
  euDeforestationRegulation: text(values.euDeforestationRegulation),
  eudrRegulations: text(values.euDeforestationRegulation),
  nonGmoFood: text(values.nonGmoFoodFeed),
  nonGmoFoodTechnicalMarket: text(values.nonGmoTechnicalMarkets),

  declarationFileId,
});
